import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, MailX, MailCheck, Loader2, AlertTriangle, CheckCircle, ArrowLeft, Bell, BellOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailPrefs() {
  const [token, setToken] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedAction, setConfirmedAction] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [reverseToken, setReverseToken] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setError('Enlace inválido. No se encontró el token de verificación.');
      setLoading(false);
      return;
    }
    setToken(t);
    axios.get(`${API_URL}/api/email/unsubscribe-info?token=${t}`)
      .then(res => { setInfo(res.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.detail || 'El enlace es inválido o ha caducado.');
        setLoading(false);
      });
  }, []);

  const handleAction = async (action) => {
    if (!token || confirming) return;
    setConfirming(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/confirm-action?token=${token}&action=${action}`);
      setConfirmed(true);
      setConfirmedAction(action);
      setReverseToken(res.data.reverse_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar la solicitud.');
    } finally {
      setConfirming(false);
    }
  };

  const pref_labels = {
    marketing: 'novedades y actualizaciones de Veritas Scan',
    blog: 'nuevos artículos del blog',
    all: 'todos los emails opcionales (novedades y blog)',
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-4 py-12 font-sans">
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59,130,246,0.06), transparent 60%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 px-8 py-8 text-center border-b border-zinc-800">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-lg">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-black tracking-tight text-white">
                VERITAS<span className="text-blue-400"> SCAN</span>
              </span>
            </div>
            <p className="text-zinc-500 text-xs tracking-widest uppercase mt-1">Preferencias de email</p>
          </div>

          {/* Body */}
          <div className="px-8 py-8">
            <AnimatePresence mode="wait">
              {loading && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-8">
                  <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
                  <p className="text-zinc-400 text-sm">Cargando tus preferencias...</p>
                </motion.div>
              )}

              {error && !loading && (
                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6">
                  <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-white text-xl font-bold mb-2">Enlace inválido</h2>
                  <p className="text-zinc-400 text-sm leading-relaxed">{error}</p>
                  <a href="https://veritas-scan.com" className="mt-6 inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Volver a Veritas Scan
                  </a>
                </motion.div>
              )}

              {!loading && !error && !confirmed && info && (
                <motion.div key="confirm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
                  {/* Current status */}
                  <div className={`flex items-center gap-3 p-4 rounded-xl border ${info.currently_subscribed ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-zinc-800 border-zinc-700'}`}>
                    {info.currently_subscribed
                      ? <Bell className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      : <BellOff className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    }
                    <div>
                      <p className="text-zinc-300 text-sm font-medium">{info.name || info.email}</p>
                      <p className="text-zinc-500 text-xs mt-0.5">
                        {info.currently_subscribed
                          ? <>Actualmente <span className="text-emerald-400">suscrito</span> a {pref_labels[info.pref_type] || info.pref_label}</>
                          : <>Actualmente <span className="text-zinc-400">no suscrito</span> a {pref_labels[info.pref_type] || info.pref_label}</>
                        }
                      </p>
                    </div>
                  </div>

                  {/* Info box */}
                  <div className="bg-zinc-800/60 rounded-xl p-4 border border-zinc-700/50">
                    <p className="text-zinc-300 text-sm font-medium mb-1">
                      {info.currently_subscribed ? '¿Quieres desuscribirte?' : '¿Quieres volver a suscribirte?'}
                    </p>
                    <p className="text-zinc-500 text-sm leading-relaxed">
                      {info.currently_subscribed
                        ? <>Dejarás de recibir emails de <strong className="text-zinc-300">{pref_labels[info.pref_type] || info.pref_label}</strong>. Los emails de cuenta (verificación, facturas, seguridad) seguirán llegando siempre.</>
                        : <>Volverás a recibir emails de <strong className="text-zinc-300">{pref_labels[info.pref_type] || info.pref_label}</strong>. Puedes cancelar de nuevo cuando quieras.</>
                      }
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="space-y-3">
                    {info.currently_subscribed ? (
                      <>
                        <button
                          onClick={() => handleAction('unsubscribe')}
                          disabled={confirming}
                          className="w-full flex items-center justify-center gap-2 py-3.5 bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white rounded-xl font-semibold text-sm transition-all"
                        >
                          {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailX className="w-4 h-4" />}
                          {confirming ? 'Procesando...' : 'Confirmar desuscripción'}
                        </button>
                        <a href="https://veritas-scan.com/perfil" className="block text-center text-zinc-500 hover:text-zinc-300 text-sm py-2 transition-colors">
                          Cancelar — gestionar desde mi perfil
                        </a>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleAction('resubscribe')}
                          disabled={confirming}
                          className="w-full flex items-center justify-center gap-2 py-3.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white rounded-xl font-semibold text-sm transition-all"
                        >
                          {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
                          {confirming ? 'Procesando...' : 'Volver a suscribirme'}
                        </button>
                        <a href="https://veritas-scan.com/perfil" className="block text-center text-zinc-500 hover:text-zinc-300 text-sm py-2 transition-colors">
                          Gestionar desde mi perfil
                        </a>
                      </>
                    )}
                  </div>
                </motion.div>
              )}

              {!loading && !error && confirmed && (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4 space-y-5">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${confirmedAction === 'unsubscribe' ? 'bg-red-500/10 border border-red-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
                    {confirmedAction === 'unsubscribe'
                      ? <MailX className="w-10 h-10 text-red-400" />
                      : <MailCheck className="w-10 h-10 text-emerald-400" />
                    }
                  </div>

                  <div>
                    <h2 className="text-white text-xl font-bold mb-2">
                      {confirmedAction === 'unsubscribe' ? '¡Desuscrito correctamente!' : '¡Suscrito de nuevo!'}
                    </h2>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      {confirmedAction === 'unsubscribe'
                        ? <>Ya no recibirás emails de <strong className="text-zinc-300">{pref_labels[info?.pref_type] || info?.pref_label}</strong>. Los emails de cuenta seguirán llegando normalmente.</>
                        : <>Volverás a recibir emails de <strong className="text-zinc-300">{pref_labels[info?.pref_type] || info?.pref_label}</strong>.</>
                      }
                    </p>
                  </div>

                  {/* Reverse action */}
                  {reverseToken && (
                    <div className="bg-zinc-800/60 rounded-xl p-4 border border-zinc-700/50 text-left">
                      <p className="text-zinc-400 text-xs mb-2">
                        {confirmedAction === 'unsubscribe' ? '¿Fue un error? Puedes volver a suscribirte:' : '¿Quieres desuscribirte de nuevo?'}
                      </p>
                      <a
                        href={`/email-prefs?token=${reverseToken}`}
                        className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                      >
                        {confirmedAction === 'unsubscribe' ? 'Revertir desuscripción →' : 'Desuscribirme →'}
                      </a>
                    </div>
                  )}

                  <a
                    href="https://veritas-scan.com"
                    className="flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-sm font-medium transition-all"
                  >
                    <ArrowLeft className="w-4 h-4" /> Volver a Veritas Scan
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 border-t border-zinc-800 text-center">
            <p className="text-zinc-600 text-xs">
              © 2026 <a href="https://veritas-scan.com" className="text-zinc-500 hover:text-zinc-300 transition-colors">veritas-scan.com</a> — AI Content Detector
            </p>
            <p className="text-zinc-700 text-xs mt-1">
              Los emails de cuenta (verificación, facturas, seguridad) no pueden desactivarse.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
